import { trackEventFx } from "../libs/analytics";

import type {
  InboxV2Conversation,
  InboxV2Feed,
  InboxV2MessageType,
  InboxV2Sentiment,
} from "./types";
import type { InboxV2StoreState } from "./data/store/state";

export const INBOX_V2_EVENTS = {
  board: "inbox-board",
  conversation: "inbox-conversation",
  feed: "inbox-feed",
  profile: "inbox-profile",
  reply: "inbox-reply",
  search: "inbox-search",
  settings: "inbox-settings",
} as const;

export interface InboxV2AnalyticsContext {
  boardId?: string;
  feedId?: string;
}

export type WithInboxV2AnalyticsContext<T> = T & {
  analyticsContext?: InboxV2AnalyticsContext;
};

type InboxV2EventName = (typeof INBOX_V2_EVENTS)[keyof typeof INBOX_V2_EVENTS];
type InboxV2EventProperties = Record<
  string,
  boolean | number | string | undefined
>;

export function trackInboxV2Event(
  eventName: InboxV2EventName,
  properties: InboxV2EventProperties
) {
  void trackEventFx({
    eventName,
    metadata: Object.fromEntries(
      Object.entries(properties).filter(([, value]) => value !== undefined)
    ),
  });
}

export function getInboxV2ConversationEventProperties({
  boardId,
  conversation,
  feedId,
  state,
}: {
  boardId?: string;
  conversation: InboxV2Conversation;
  feedId?: string;
  state?: InboxV2StoreState;
}) {
  const platform =
    conversation.platform ??
    state?.accounts.find((account) => account.id === conversation.accountId)
      ?.platform;

  return {
    board_id: boardId,
    conversation_id: conversation.id,
    feed_id: feedId,
    message_type:
      platform === "google-business" && conversation.messageType === "review"
        ? "gmb-review"
        : normalizeInboxV2MessageType(conversation.messageType),
    platform,
    sentiment: normalizeInboxV2Sentiment(conversation.sentimentType),
  };
}

export function getInboxV2ReplyEventProperties(
  input: Parameters<typeof getInboxV2ConversationEventProperties>[0]
) {
  const properties = getInboxV2ConversationEventProperties(input);

  return {
    ...properties,
    message_type:
      properties.message_type === "gmb-review"
        ? "review"
        : properties.message_type,
  };
}

export type InboxV2DraftAction =
  "draft-save" | "draft-send" | "draft-edit" | "draft-discard";
export type InboxV2DraftAuthorType = "user" | "integration";

export function trackInboxV2DraftAction(
  action: InboxV2DraftAction,
  context: Parameters<typeof getInboxV2ReplyEventProperties>[0],
  draftAuthorType: InboxV2DraftAuthorType = "user"
) {
  trackInboxV2Event(INBOX_V2_EVENTS.reply, {
    ...getInboxV2ReplyEventProperties(context),
    action,
    draft_author_type: draftAuthorType,
  });
}

export function trackInboxV2ConversationActions(
  action:
    | "assign"
    | "close"
    | "mark-read"
    | "mark-unread"
    | "reopen"
    | "star"
    | "tag"
    | "unassign"
    | "unstar"
    | "untag",
  conversationIds: string[],
  state: InboxV2StoreState,
  analyticsContext?: InboxV2AnalyticsContext
) {
  conversationIds.forEach((conversationId) => {
    const conversation = state.conversationsById[conversationId];

    if (!conversation) return;

    trackInboxV2Event(INBOX_V2_EVENTS.conversation, {
      action,
      ...getInboxV2ConversationEventProperties({
        boardId: analyticsContext?.boardId,
        conversation,
        feedId: analyticsContext?.feedId,
        state,
      }),
    });
  });
}

export function trackInboxV2FeedMutation({
  action,
  feed,
}: {
  action: "create" | "edit";
  feed: InboxV2AnalyticsFeed;
}) {
  trackInboxV2Event(INBOX_V2_EVENTS.feed, {
    action,
    board_id: feed.boardId,
    feed_id: feed.id,
    feed_name: feed.name,
  });
}

export function trackInboxV2FeedFilters({
  feed,
  previousFeed,
}: {
  feed: InboxV2AnalyticsFeed;
  previousFeed?: InboxV2AnalyticsFeed;
}) {
  if (!feed.id) return;

  const baseProperties = {
    board_id: feed.boardId,
    feed_id: feed.id,
    feed_name: feed.name,
  };

  const activeFilters = getActiveFeedFilters(feed);
  const previousFilters = previousFeed
    ? new Map(
        getActiveFeedFilters(previousFeed).map((filter) => [
          filter.type,
          filter.value,
        ])
      )
    : new Map<string, string>();

  activeFilters.forEach((filter) => {
    if (previousFilters.get(filter.type) === filter.value) return;

    trackInboxV2Event(INBOX_V2_EVENTS.feed, {
      action: "filter-applied",
      ...baseProperties,
      filter_count: activeFilters.length,
      filter_type: filter.type,
      filter_value: filter.value,
    });
  });
}

export function normalizeInboxV2MessageType(
  messageType: Exclude<InboxV2MessageType, "all">
) {
  switch (messageType) {
    case "dm": {
      return "dm";
    }
    case "mention":
    case "mention-in-caption":
    case "tagged-in-media":
    case "tagged-post": {
      return "mention";
    }
    case "review":
    case "qa": {
      return "review";
    }
    default: {
      return "comment";
    }
  }
}

function normalizeInboxV2Sentiment(sentiment?: InboxV2Sentiment) {
  return sentiment === "unknown" ? undefined : sentiment;
}

function getActiveFeedFilters(
  feed: InboxV2AnalyticsFeed
): Array<{ type: string; value: string }> {
  const filters: Array<{ type: string; value: string }> = [];
  const addMatchFilter = (
    type: string,
    filter: { match: "all" } | { match: "or"; values: string[] } | undefined
  ) => {
    if (filter?.match === "or" && filter.values.length > 0) {
      filters.push({ type, value: filter.values.join(",") });
    }
  };

  addMatchFilter("account", feed.filters.accountIds);
  addMatchFilter("message-type", feed.filters.messageType);
  addMatchFilter("status", feed.filters.status);
  addMatchFilter("sentiment", feed.filters.sentiment);
  addMatchFilter("tag", feed.filters.tags);
  addMatchFilter("assigned-to", feed.filters.assignee);
  addMatchFilter("starred", feed.filters.saved);
  addMatchFilter("has-draft", feed.filters.hasDraft);

  const { dateFrom, dateTo } = feed.filters.dateRange ?? {};
  if (dateFrom || dateTo) {
    filters.push({
      type: "date",
      value: `${dateFrom ?? ""}:${dateTo ?? ""}`,
    });
  }

  return filters;
}

export type InboxV2AnalyticsFeed = Pick<
  InboxV2Feed,
  "boardId" | "filters" | "name"
> & {
  id?: string;
};
